import React, { useState, useEffect, useRef } from "react";
import { LinearGradient } from 'react-text-gradients'
import ReactMarkdown from "react-markdown";
import { v4 as uuidv4 } from "uuid";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Container,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { backendServerUrl } from "../config";
import ResultCard from './ResultCard';
import FeedbackButtons from './FeedbackButtons';

export const formatEcliParts = (ecli) => {
  if (!ecli || typeof ecli !== "string" || !ecli.startsWith("ECLI:")) return "";

  const parts = ecli.split(":");
  if (parts.length < 5) return "";

  const [_, land, gericht, jahr, ...rest] = parts;
  const aktenzeichen = rest.join(":");

  return [
    `Land: ${land}`,
    `Gericht: ${gericht}`,
    `Jahr: ${jahr}`,
    `Aktenzeichen: ${aktenzeichen}`
  ].join(", ");
};


export const handleCopy = (text, setCopied) => {
  if (!text) return;
  navigator.clipboard.writeText(text);
  setCopied(true);
  setTimeout(() => setCopied(false), 2000);
};

function LLMAntwort({
  answer,
  llmFeedback,
  setLlmFeedback,
  llmFeedbackText,
  setLlmFeedbackText,
}) {
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);

  const onCopyClick = () => handleCopy(answer, setCopied);

  useEffect(() => {
    if (userHasInteracted) return; // Don't auto-collapse if user has manually interacted

    const handleScroll = () => {
      const searchSection = document.getElementById('searchSection');
      const resultsSection = document.querySelector('[data-results-section]');
      
      if (searchSection && resultsSection) {
        const searchSectionRect = searchSection.getBoundingClientRect();
        const resultsSectionRect = resultsSection.getBoundingClientRect();
        
        // Collapse if user has scrolled down to the results section
        if (resultsSectionRect.top < window.innerHeight * 0.7 && expanded) {
          setExpanded(false);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [expanded, userHasInteracted]);

  const handleAccordionChange = () => {
    setUserHasInteracted(true);
    setExpanded(!expanded);
  };

  return (
    <Box
      sx={{
        position: "sticky",
        top: "140px",
        zIndex: 10,
        paddingBottom: "1rem",
      }}
    >      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        disableGutters
        sx={{ marginBottom: 0, background: "linear-gradient(to right, #f2f1fa, #ebf1ed)" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon
              fontSize="small"
              sx={{ color: "#7971CA" }}
              titleAccess="AI-generierte Zusammenfassung"
            />
            <Typography variant="h6" color="text.primary">
              <LinearGradient
                gradient={["to right", "#7971CA, #244D33"]}
                fallbackColor="text.primary"
              >
                KI generierte Antwort:
              </LinearGradient>
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
            <Typography
              sx={{
                variant: "body1",
                fontFamily: "Inter, sans-serif",
                marginLeft: "24px",
                paddingTop: 0,
              }}
            >
              <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                }}
              >
                {answer}
              </ReactMarkdown>
            </Typography>
            <Tooltip title={copied ? "Kopiert!" : "In Zwischenablage kopieren"}>
              <IconButton
onClick={onCopyClick}
size="small"
sx={{ marginTop: "4px" }}
>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <FeedbackButtons
            feedback={llmFeedback}
            onFeedback={(type) => setLlmFeedback(type)}
          />

          {llmFeedback === "down" && (
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Dein Feedback zur KI-Antwort"
                variant="outlined"
                multiline
                rows={2}
                fullWidth
                value={llmFeedbackText}
                onChange={(e) => setLlmFeedbackText(e.target.value)}
              />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const SearchSection = ({
  query,
  setQuery,
  previousSearches,
  anchorEl,
  handleOpenMenu,
  handleCloseMenu,
  handleSearch,
  results,
  loading,
  answer,
  answerLoading,
  searchId
}) => {
  const [searchScope, setSearchScope] = useState("leitsaetze");
  const [feedbackData, setFeedbackData] = useState([]);
  const [llmFeedback, setLlmFeedback] = useState("none");
  const [llmFeedbackText, setLlmFeedbackText] = useState("");
  const [oldQuery, setOldQuery] = useState(query);

  const handleFeedback = (resultObject, feedbackType, resultIndex) => {
    setFeedbackData((prev) => {
      const updated = prev.filter((f) => f.resultIndex !== resultIndex);
      updated.push({ resultIndex, feedback_type: feedbackType });
      return updated;
    });
  };

  const submitFeedback = async () => {
    if (results.length === 0) return;
    const feedbackList = results.map((_, idx) => {
      const feedbackItem = feedbackData.find((f) => f.resultIndex === idx);
      return feedbackItem ? feedbackItem.feedback_type : null;
    });

    try {
      const token = localStorage.getItem("authToken");
      await fetch(`${backendServerUrl}/api/search-result-feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          search_id: searchId,
          email: localStorage.getItem("authTokenEmail"),
          search_query: oldQuery,
          results,
          feedback_list: feedbackList,
          llm_feedback: llmFeedback === "none" ? null : llmFeedback,
          llm_feedback_text: llmFeedbackText || null,
          llm_response: answer || null,
        }),
      });
    } catch (error) {
      console.error("Error submitting feedback:", error);
    }
  };

  const handleNewSearch = (e, searchScope) => {
    e.preventDefault();
    setOldQuery(query);
    // Before new search, submit any existing feedback
    submitFeedback().then(() => {
      // Reset feedback states
      setFeedbackData([]);
      setLlmFeedback("none");
      setLlmFeedbackText("");

      // Then call the real search
      handleSearch(e, searchScope);
    });
  };

  return (
    <Box
      id="searchSection"
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "90vh",
        px: 2,
        mt: "64px",
        transition: "transform 0.4s ease-in-out",
        transform:
          query && (loading || results.length > 0)
            ? "translateY(-40px)"
            : "translateY(0)",
      }}
    >
      <Container maxWidth="md" sx={{ mt: 4, overflow: "visible" }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary.dark">
          Suche nach relevanten Datenschutzentscheidungen
        </Typography>

        {/* SEARCH BAR */}
        <Box
          component="form"
          onSubmit={(e) => handleNewSearch(e, searchScope)}
          sx={{ display: "flex", gap: 2, marginBottom: "1rem" }}
        >
          <IconButton
            disabled={previousSearches.length === 0}
            onClick={(e) => {
              if (previousSearches.length === 0) return;
              handleOpenMenu(e);
            }}
          >
            <HistoryIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: "200px",
                  width: "20ch",
                  overflowY: "auto",
                  backgroundColor: "background.paperTransparent",
                },
              },
            }}
          >
            {previousSearches.slice(0, 20).map((search, idx) => (
              <Tooltip title={search} placement="right" followCursor>
              <MenuItem
                key={idx}
                onClick={() => {
                  setQuery(search);
                  handleCloseMenu();
                }}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    flexGrow: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {search}
                </Typography>
              </MenuItem>
              </Tooltip>
            ))}
          </Menu>

          <Box sx={{ position: "relative", width: "100%" }}>            <TextField
              fullWidth
              variant="outlined"
              label="Frage eingeben..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              multiline
              maxRows={11}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSearch(e, searchScope);
                }
              }}
              sx={{
                "& .MuiInputBase-root": { overflowY: "auto", paddingBottom: "20px", borderRadius: "16px"},
                "& .MuiOutlinedInput-root": { borderRadius: "16px", "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#346E4A",},},
              }}
            />            <IconButton
              type="submit"
              color="primary"
              sx={{
                position: "absolute",
                top: "50%",
                right: 8,
                transform: "translateY(-50%)",
                backgroundColor: "secondary.light",
                color: "white",
                "&:hover": {
                  backgroundColor: "secondary.dark",
                },
              }}
            >
              <AutoAwesomeIcon />
            </IconButton>
          </Box>
        </Box>

        {/* ANSWER SECTION */}
        {answerLoading && query && !answer ? (
          <Card
            variant="outlined"
            sx={{
              marginBottom: "2rem",
              padding: "1rem",
              backgroundColor: "background.paper",
              boxShadow: 3,
            }}
          >
            <CardContent>
              <Skeleton variant="text" width="50%" height={30} />
              <Skeleton variant="text" width="80%" height={20} />
              <Skeleton variant="rectangular" width="100%" height={60} />
            </CardContent>
          </Card>
        ) : answer && (
          <LLMAntwort
            answer={answer}
            llmFeedback={llmFeedback}                  
            setLlmFeedback={setLlmFeedback}            
            llmFeedbackText={llmFeedbackText}          
            setLlmFeedbackText={setLlmFeedbackText}    
          />
        )}        {/* RESULTS SECTION */}
        <Box sx={{ mt: 2 }} data-results-section>
          {loading && query ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <Card
                variant="outlined"
                key={idx}
                sx={{
                  marginBottom: "1rem",
                  opacity: 1,
                  animation: `fadeIn 0.4s ease-in-out forwards`,
                  animationDelay: `${idx * 100}ms`,
                }}
              >
                <CardContent>
                  <Skeleton variant="text" width="30%" height={30} />
                  <Skeleton variant="text" width="50%" height={20} />
                  <Skeleton variant="rectangular" width="100%" height={60} />
                </CardContent>
              </Card>
            ))          ) : results.length > 0 ? (
            results.map((item, idx) => (
              <ResultCard
                key={idx}
                item={item}
                onFeedback={handleFeedback}
                resultIndex={idx}
              />
            ))
          ) : (
            <Typography variant="body1" color="text.secondary">
              Keine Ergebnisse gefunden.
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default SearchSection;
